import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Pagination,
  Row,
  Spin,
  Space,
  Tag,
  Typography,
  Tooltip,
  message,
} from 'antd'
import {
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { NoData } from '~/Components/NoData'
import { WaitOverlay } from '~/Components/Overlays/WaitOverlay'
import { ScrollBar } from '~/Components/ScrollBar'
import { Client } from '~/Services/Clients'
import { ClientActions } from '~/Store/Client'
import {
  getClientDetailLoading,
  getClientError,
  getClientLoading,
  getClientSubmitting,
  getListClients,
  getSelectedClient,
} from '~/Store/Client/selectors'
import { useAppDispatch, useAppSelector } from '~/Store/hooks'
import { getLeftSiderCollapsed } from '~/Store/Layout/selectors'
import styles from '../Devices/Devices.module.less'
import {
  ClientFormModal,
  ClientFormValues,
  DEFAULT_FORM_VALUES,
} from './ClientModals'
import { ClientDetailModal } from './ClientDetailModal'

export const Clients = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [searchText, setSearchText] = useState('')
  const [isFormVisible, setIsFormVisible] = useState(false)
  const [isDetailVisible, setIsDetailVisible] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [form] = Form.useForm<ClientFormValues>()

  const clients = useAppSelector(getListClients)
  const selectedClient = useAppSelector(getSelectedClient)
  const loading = useAppSelector(getClientLoading)
  const detailLoading = useAppSelector(getClientDetailLoading)
  const submitting = useAppSelector(getClientSubmitting)
  const error = useAppSelector(getClientError)
  const leftMenuCollapsed = useAppSelector(getLeftSiderCollapsed)
  const waitOverlayName = 'fetchingClients'

  useEffect(() => {
    dispatch(ClientActions.getClientsRequest())
  }, [dispatch])

  useEffect(() => {
    if (!isFormVisible) {
      form.resetFields()
      return
    }

    if (!editingClient) {
      form.setFieldsValue(DEFAULT_FORM_VALUES)
      return
    }

    form.setFieldsValue({
      clientId: editingClient.clientId,
      clientName: editingClient.clientName,
      allowOfflineAccess: editingClient.allowOfflineAccess,
      allowAccessTokensViaBrowser: editingClient.allowAccessTokensViaBrowser,
      updateAccessTokenClaimsOnRefresh:
        editingClient.updateAccessTokenClaimsOnRefresh,
      refreshTokenExpiration: editingClient.refreshTokenExpiration,
      enabled: editingClient.enabled,
      requireClientSecret: editingClient.requireClientSecret,
    })
  }, [editingClient, form, isFormVisible])

  const filteredClients = useMemo(() => {
    const normalizedKeyword = searchText.trim().toLowerCase()

    if (!normalizedKeyword) {
      return clients
    }

    return clients.filter(client => {
      const id = client.clientId?.toLowerCase() || ''
      const name = client.clientName?.toLowerCase() || ''
      return id.includes(normalizedKeyword) || name.includes(normalizedKeyword)
    })
  }, [clients, searchText])

  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredClients.slice(startIndex, startIndex + pageSize)
  }, [currentPage, filteredClients, pageSize])

  const openCreateModal = () => {
    setEditingClient(null)
    setIsFormVisible(true)
  }

  const openEditModal = (client: Client) => {
    setEditingClient(client)
    setIsFormVisible(true)
  }

  const closeFormModal = () => {
    setIsFormVisible(false)
    setEditingClient(null)
    form.resetFields()
    dispatch(ClientActions.clearClientError())
  }

  const openDetailModal = (clientId: string) => {
    setIsDetailVisible(true)
    dispatch(
      ClientActions.getClientByIdRequest({
        clientId,
        onFailure: errorMessage => {
          message.error(errorMessage)
        },
      }),
    )
  }

  const closeDetailModal = () => {
    setIsDetailVisible(false)
    dispatch(ClientActions.clearSelectedClient())
  }

  const changePage = (page: number, nextPageSize?: number) => {
    setCurrentPage(page)
    if (nextPageSize && nextPageSize !== pageSize) {
      setPageSize(nextPageSize)
    }
  }

  const submitForm = () => {
    form.submit()
  }

  const handleSubmit = (values: ClientFormValues) => {
    if (editingClient) {
      dispatch(
        ClientActions.updateClientRequest({
          clientId: editingClient.clientId,
          data: {
            clientId: editingClient.clientId,
            clientName: values.clientName.trim(),
            allowOfflineAccess: values.allowOfflineAccess,
            allowAccessTokensViaBrowser: values.allowAccessTokensViaBrowser,
            updateAccessTokenClaimsOnRefresh:
              values.updateAccessTokenClaimsOnRefresh,
            refreshTokenExpiration: values.refreshTokenExpiration,
            enabled: values.enabled,
            requireClientSecret: values.requireClientSecret,
          },
          onSuccess: () => {
            message.success(t('admin.clientUpdatedSuccess'))
            closeFormModal()
          },
          onFailure: errorMessage => {
            message.error(errorMessage)
          },
        }),
      )
      return
    }

    dispatch(
      ClientActions.createClientRequest({
        data: {
          clientId: values.clientId.trim(),
          clientName: values.clientName.trim(),
        },
        onSuccess: () => {
          message.success(t('admin.clientCreatedSuccess'))
          closeFormModal()
        },
        onFailure: () => {
          closeFormModal()
        },
      }),
    )
  }

  return (
    <WaitOverlay
      name={waitOverlayName}
      size="large"
      wrapperClassName={styles.FullOverlay}
    >
      <div className={styles.OrganizationUsers}>
        <Row align="stretch" gutter={10}>
          <Typography.Title style={{ flex: 1 }} level={4}>
            {t('admin.clients')}
          </Typography.Title>
          <Col>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('admin.search')}
              value={searchText}
              onChange={event => {
                setSearchText(event.target.value)
                setCurrentPage(1)
              }}
              style={{ marginBottom: '24px' }}
            />
          </Col>
          <Col>
            <Tooltip title={t('admin.createClient')}>
              <Button
                icon={<PlusOutlined style={{ fontSize: '12px' }} />}
                onClick={openCreateModal}
              />
            </Tooltip>
          </Col>
        </Row>

        <div className={styles.UserList}>
          <ScrollBar style={{ paddingRight: '16px', marginRight: '-16px' }}>
            <div className={styles.UserItem} style={{ fontWeight: 'bold' }}>
              <Row align="middle">
                <Col xs={6} md={6}>
                  {t('admin.clientId')}
                </Col>
                <Col xs={12} md={12}>
                  {t('admin.clientName')}
                </Col>
                <Col xs={4} md={4} style={{ textAlign: 'center' }}>
                  {t('admin.status')}
                </Col>
                <Col xs={2} md={2} style={{ textAlign: 'center' }}>
                  <Tooltip title={t('actions')}>
                    <SettingOutlined />
                  </Tooltip>
                </Col>
              </Row>
            </div>
            <Spin spinning={loading}>
              {paginatedClients.length > 0 ? (
                paginatedClients.map(item => (
                  <div
                    key={item.clientId}
                    className={styles.UserItem}
                    style={{
                      background: item.enabled
                        ? 'transparent'
                        : 'rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <Row gutter={[8, 8]} align="middle">
                      <Col xs={6} md={6}>
                        {item.clientId}
                      </Col>
                      <Col xs={12} md={12}>
                        {item.clientName}
                      </Col>
                      <Col
                        xs={4}
                        md={4}
                        style={{ textAlign: 'center', paddingTop: '5px' }}
                      >
                        {item.enabled ? (
                          <Tag color="green">Enabled</Tag>
                        ) : (
                          <Tag color="red">Disabled</Tag>
                        )}
                      </Col>
                      <Col xs={2} md={2} style={{ textAlign: 'center' }}>
                        <Space
                          size="small"
                          style={{ justifyContent: 'center' }}
                        >
                          <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => openDetailModal(item.clientId)}
                          />
                          <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(item)}
                          />
                        </Space>
                      </Col>
                    </Row>
                  </div>
                ))
              ) : (
                <NoData content={t('admin.noData')} />
              )}
            </Spin>
          </ScrollBar>
        </div>
        <div
          className={styles.Pagination}
          style={{
            width: `calc(100% - ${leftMenuCollapsed ? '146' : '266'}px)`,
          }}
        >
          <Pagination
            current={currentPage}
            total={filteredClients.length}
            pageSize={pageSize}
            showSizeChanger
            onChange={changePage}
          />
        </div>
      </div>

      <ClientFormModal
        editingClient={editingClient}
        visible={isFormVisible}
        submitting={submitting}
        form={form}
        onCancel={closeFormModal}
        onSubmit={submitForm}
        onFinish={handleSubmit}
      />

      <ClientDetailModal
        client={selectedClient}
        detailLoading={detailLoading}
        visible={isDetailVisible}
        onCancel={closeDetailModal}
      />
    </WaitOverlay>
  )
}

